import { useMemo } from "react";
import { cn } from "../../lib/utils";

const SEV_COLOR = { CRITICAL: "#EF4444", HIGH: "#F97316", MEDIUM: "#FBBF24", LOW: "#64748B" };

function nodeStyle(n, selected) {
  if (n.type === "owner") return { fill: "#F59E0B", r: 26, stroke: "#F59E0B" };
  if (n.type === "person") return { fill: "#0F1117", r: 18, stroke: "#06B6D4" };
  if (n.type === "process") return { fill: "#0F1117", r: 14, stroke: "rgba(255,255,255,0.35)" };
  const status = (n.status || "OPEN").toUpperCase();
  let c = SEV_COLOR[n.severity] || "#64748B";
  if (status === "CONTROLLED" || status === "RESOLVED") c = "#10B981";
  else if (status === "MITIGATION_ACTIVE" || status === "IN_TEST") c = "#F97316";
  return { fill: "#0F1117", r: 15, stroke: c };
}

export function DependencyGraphView({ nodes = [], edges = [], selectedNodeId, onSelectNode }) {
  const W = 720, H = 520, cx = W / 2, cy = H / 2;

  const positions = useMemo(() => {
    const pos = {};
    pos["owner"] = { x: cx, y: cy };
    const groups = { person: [], process: [], dependency: [] };
    nodes.forEach((n) => { if (n.type !== "owner") (groups[n.type] || (groups[n.type] = [])).push(n); });
    const rings = [
      { key: "dependency", r: 150 },
      { key: "process", r: 235 },
      { key: "person", r: 235 },
    ];
    // people + processes share outer ring
    const outer = [...groups.person, ...groups.process];
    const place = (arr, r, offset = 0) => {
      const step = (2 * Math.PI) / Math.max(1, arr.length);
      arr.forEach((n, i) => {
        const a = offset + i * step - Math.PI / 2;
        pos[n.id] = { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
      });
    };
    place(groups.dependency, 150, 0.2);
    place(outer, 235, 0.6);
    return pos;
  }, [nodes]);

  if (!nodes.length) return null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: 560 }}>
      <defs>
        <radialGradient id="ownerGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(245,158,11,0.35)" />
          <stop offset="100%" stopColor="rgba(245,158,11,0)" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={70} fill="url(#ownerGlow)" />
      {edges.map((e, i) => {
        const s = positions[e.source], t = positions[e.target];
        if (!s || !t) return null;
        return (
          <line key={i} x1={s.x} y1={s.y} x2={t.x} y2={t.y}
            stroke={e.kind === "depends" ? "rgba(245,158,11,0.28)" : "rgba(255,255,255,0.1)"}
            strokeWidth={e.kind === "depends" ? 1.5 : 1} />
        );
      })}
      {nodes.map((n) => {
        const p = positions[n.id];
        if (!p) return null;
        const st = nodeStyle(n, selectedNodeId === n.id);
        const sel = selectedNodeId === n.id;
        return (
          <g key={n.id} transform={`translate(${p.x},${p.y})`}
            className="cursor-pointer" onClick={() => onSelectNode?.(n)}
            data-testid={`graph-node-${n.id}`}>
            {sel && <circle r={st.r + 7} fill="none" stroke={st.stroke} strokeWidth="1" opacity="0.5" />}
            <circle r={st.r} fill={st.fill} stroke={st.stroke} strokeWidth={sel ? 3 : 2} />
            <text y={st.r + 15} textAnchor="middle" fill="#94A3B8"
              fontSize="11" fontFamily="Plus Jakarta Sans" className="pointer-events-none">
              {(n.label || "").slice(0, 22)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function GraphLegend() {
  const items = [
    { c: "#F59E0B", l: "Propietario" }, { c: "#06B6D4", l: "Persona" },
    { c: "#EF4444", l: "Dependencia crítica" }, { c: "#F97316", l: "Mitigación en prueba" },
    { c: "#10B981", l: "Controlada" },
  ];
  return (
    <div className="flex flex-wrap gap-4">
      {items.map((it) => (
        <span key={it.l} className="inline-flex items-center gap-2 text-xs text-slate-400">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: it.c }} />{it.l}
        </span>
      ))}
    </div>
  );
}
